/**
 * RobiQuickAnswerModal — the "Ask anything!" chat. Same conversation surface
 * as a Messages thread, hosted in a dialog because it opens over the feed
 * (mobile shows it as a bottom sheet). Opening reloads the stored thread, so
 * only messages the backend persisted survive a close.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import useQuickAnswerChat from '@wlb/shared/hooks/useQuickAnswerChat';
import useQuickAnswerConversation from '@wlb/shared/hooks/useQuickAnswerConversation';
import { resolveProfilePictureUrl } from '@wlb/shared/utils/imageUrl';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ChatBubble } from '../messages/ChatBubble';
import { ChatInput } from '../messages/ChatInput';
import { ChatSkeleton } from '../messages/ChatSkeleton';
import { buildChatTimeline } from '../messages/chatTimeline';
import robiIcon from '../../assets/icons/robi-mobile.png';
import '../messages/messages.css';
import './robi.css';

interface RobiQuickAnswerModalProps {
  onClose: () => void;
}

export function RobiQuickAnswerModal({ onClose }: RobiQuickAnswerModalProps) {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const { conversationId, conversationDetails, loading, error, retry } =
    useQuickAnswerConversation(true);

  // Participant order is not guaranteed, so identify Robi as the member that
  // is not the signed-in user rather than trusting an index.
  const agentParticipant = useMemo(() => {
    const participants = conversationDetails?.participants ?? [];
    return (
      participants.find(
        participant => String(participant.userId) !== String(profile?.userId),
      ) ?? null
    );
  }, [conversationDetails?.participants, profile?.userId]);

  const agentAvatar =
    resolveProfilePictureUrl(agentParticipant?.profileImageUrl) || robiIcon;
  const agentName =
    agentParticipant?.firstName?.trim() ||
    agentParticipant?.userName?.trim() ||
    t('robi.quickAnswer');

  const agent = useMemo(
    () => ({
      _id: agentParticipant?.userId ?? 'robi',
      name: agentName,
      avatar: agentAvatar,
    }),
    [agentParticipant?.userId, agentName, agentAvatar],
  );

  const { messages, currentUser, loadingHistory, sending, send } = useQuickAnswerChat({
    conversationId,
    agent,
  });

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const timeline = useMemo(() => buildChatTimeline(messages, t), [messages, t]);

  const starterQuestions = useMemo(
    () => [t('robi.quickReplies.howToPost'), t('robi.quickReplies.howBuddyWorks')],
    [t],
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [timeline, sending]);

  const handleSend = (text: string, clearInput: boolean) => {
    const trimmed = text.trim();
    if (!trimmed || !conversationId || sending) return;
    if (clearInput) setInputValue('');
    // The hook reports failure instead of throwing; put the text back so the
    // user does not have to retype it.
    send(trimmed)
      .then(ok => {
        if (!ok && clearInput) setInputValue(current => current || trimmed);
      })
      .catch(() => undefined);
  };

  const renderBody = () => {
    if (error) {
      return (
        <div className="chat-error">
          <p className="chat-error__text">{t('robi.errors.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={retry}>
            {t('chat.retry')}
          </Button>
        </div>
      );
    }

    if ((loading || loadingHistory) && messages.length === 0) {
      return <ChatSkeleton bubbles={4} />;
    }

    return (
      <div className="chat-messages" role="log" aria-live="polite">
        {messages.length === 0 && (
          <div className="robi-intro">
            <img className="robi-intro__avatar" src={agentAvatar} alt="" aria-hidden />
            <p className="robi-intro__message">{t('robi.welcomeMessage')}</p>
            <div
              className="robi-intro__starters"
              role="group"
              aria-label={t('robi.accessibility.quickReplies')}
            >
              {starterQuestions.map(question => (
                <button
                  key={question}
                  type="button"
                  className="robi-starter"
                  onClick={() => handleSend(question, false)}
                  disabled={!conversationId || sending}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {timeline.map(item =>
          item.type === 'day' ? (
            <div key={item.key} className="chat-day-separator">
              {item.label}
            </div>
          ) : (
            <ChatBubble
              key={item.key}
              message={item.message}
              isSent={String(item.message.user?._id) === String(currentUser._id)}
            />
          ),
        )}

        {sending && (
          <div className="chat-bubble-row chat-bubble-row--received">
            <div className="chat-bubble chat-bubble--received robi-typing" role="status">
              <span className="sr-only">{t('robi.thinking')}</span>
              <span className="robi-typing__dot" aria-hidden />
              <span className="robi-typing__dot" aria-hidden />
              <span className="robi-typing__dot" aria-hidden />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    );
  };

  return (
    <Modal
      className="robi-qa"
      labelledBy="robi-qa-title"
      onClose={onClose}
      title={
        <span className="robi-qa__identity">
          <img className="robi-qa__avatar" src={agentAvatar} alt="" aria-hidden />
          <span className="robi-qa__heading">
            <span className="robi-qa__name">{agentName}</span>
            <span className="robi-qa__tagline">{t('robi.supportTagline')}</span>
          </span>
        </span>
      }
    >
      <div className="robi-qa__body">
        {renderBody()}
        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSend={() => handleSend(inputValue, true)}
          sending={sending || !conversationId}
        />
      </div>
    </Modal>
  );
}
